import React, { useState } from "react";
import { Link } from "react-router-dom";
import BientotModal from "./BientotModal";
import { isVilleAvailable } from "../data/grandesVillesIndex";

/**
 * CityCardLink
 * ------------
 * Wrapper qui remplace <Link> dans les cartes-villes des 11 mini-threads.
 *
 *  - Si la ville a une page "grande ville" dédiée (`available: true` dans
 *    grandesVillesData.js) → rend un <Link> normal.
 *  - Sinon → rend un <button> qui ouvre la <BientotModal /> "La page dédiée
 *    à {ville} arrive très prochainement".
 *
 * Props identiques à <Link> + méta info ville :
 *  - to (string)          : chemin cible (ex: "/voyance-honfleur-14?from=…")
 *  - villeName (string)   : nom lisible ("Honfleur")
 *  - deptCode (string)    : "14"
 *  - deptName (string)    : "Calvados"
 *  - source (string)      : id de source pour le lead (ex: "mini-thread-normands")
 *  - kind ("voyance"|"energ") : oriente le lien "notre annuaire" (default: "voyance")
 *  - className, style, children, data-testid, ...rest : passés au <Link>/<button>
 */
const CityCardLink = ({
  to,
  villeName,
  deptCode,
  deptName,
  source = "mini-thread",
  kind = "voyance",
  className,
  style,
  children,
  ...rest
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  // PRD 20/02/2026 — Injection automatique de ?from=thread-XXX pour que le bouton "Retour mini-thread"
  // (RetourMiniThreadButton) apparaisse sur la page de la ville, quel que soit son pattern d'URL.
  const finalTo = (() => {
    if (!to || typeof to !== "string") return to;
    if (!source || !source.startsWith("mini-thread-")) return to;
    if (to.includes("from=")) return to;
    const fromParam = `thread-${source.replace(/^mini-thread-/, "")}`;
    const separator = to.includes("?") ? "&" : "?";
    return `${to}${separator}from=${fromParam}`;
  })();
  const available = isVilleAvailable(finalTo);

  if (available) {
    return (
      <Link to={finalTo} className={className} style={style} {...rest}>
        {children}
      </Link>
    );
  }

  // Ville non disponible → bouton qui ouvre la modale
  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className={className}
        style={{
          ...style,
          cursor: "pointer",
          // Utiliser backgroundColor (et non background) pour ne PAS écraser
          // le dégradé tailwind (background-image) appliqué via className.
          backgroundColor: style?.backgroundColor || "transparent",
          padding: style?.padding ?? 0,
          textAlign: "inherit",
          width: "100%",
          font: "inherit",
          color: "inherit",
        }}
        data-testid={rest["data-testid"] || `city-card-bientot-${villeName}`}
        aria-label={`${villeName} — page à venir, laissez-nous votre email pour être prévenu`}
      >
        {children}
      </button>
      <BientotModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        ville={villeName}
        deptCode={deptCode}
        deptName={deptName}
        source={source}
        kind={kind}
      />
    </>
  );
};

export default CityCardLink;
